import moment from "moment";

/*
  The wire format, not a display one: every caller feeds this straight into a start_date or
  end_date the services parse. It is not the shape dates are shown in - that is
  getFormatedBusinessDate below - and changing it here would change what the API is asked
  for, not what the reader sees.
*/
export const getFormatedDate = (date) => {
  return moment(new Date(date)).format("YYYY-MM-DD");
};

/*
  The one shape a displayed date takes. The subscription screens read every date through
  here - the listings, the profile tab, and the plan and corporate offer dialogs - so they
  agree with each other. The rest of the dashboard still prints what its own service sends;
  this is where those screens should come when they are brought in line, rather than each
  growing its own reading of an ISO string.

  Note it deliberately says nothing about time. Every date it is given is a date-level fact -
  an expiry, an activation, a validity window. A column where the time is the point, like an
  order's pickup or a push's click, wants its own formatter rather than this one.

  Two things it settles beyond the format:

  Timezone. The services run on Asia/Kuwait and stamp the end of a day - an expiry is
  23:59:59.999 there, which travels as 20:59:59.999Z. Read in the browser's own zone that
  tips into the next day for anyone east of +03, so a plan good through the 3rd would be
  listed as the 4th. The offset is fixed rather than local for that reason; Kuwait keeps no
  DST, so +03 is exact all year.

  Emptiness. Empty in, empty out - an expiry is null until the plan is first used, and those
  rows should stay blank rather than read "Invalid date". Callers wanting a dash say so.
*/
const KUWAIT_UTC_OFFSET_HOURS = 3;
const DISPLAY_DATE_FORMAT = "DD-MM-YYYY";

export const getFormatedBusinessDate = (date) => {
  if (!date) {
    return "";
  }

  const parsed = moment.utc(date);

  // Anything this cannot read is handed back untouched rather than printed as "Invalid
  // date". Columns carry whatever their own service sends, and a value that already reads
  // well should survive being passed through here.
  if (!parsed.isValid()) {
    return date;
  }

  return parsed.utcOffset(KUWAIT_UTC_OFFSET_HOURS).format(DISPLAY_DATE_FORMAT);
};

/*
  The three below are the "time is the point" counterpart getFormatedBusinessDate above
  excuses itself from, for the one place a reader picks an instant rather than a day: the
  delta sync window.

  They all sit on the same decision. A datetime-local value carries no zone - it is only
  what the picker showed - so something has to say which zone that was. It is read as
  Kuwait, not the browser's, for the same reason the display formatter pins +03: the
  services stamp Asia/Kuwait, and an operator asking for "since 2pm" means 2pm in the
  store, whatever zone the laptop they typed it on happens to be in.
*/
const BUSINESS_LOCAL_FORMATS = ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"];
const DISPLAY_DATE_TIME_FORMAT = "DD-MM-YYYY HH:mm";

// Picker value -> epoch milliseconds, which is what the API takes. Null for blank or
// unparseable, so a caller can tell "left empty" from "typed something wrong".
export const getEpochMsFromBusinessLocal = (value) => {
  if (!value) {
    return null;
  }

  const parsed = moment
    .utc(value, BUSINESS_LOCAL_FORMATS)
    .subtract(KUWAIT_UTC_OFFSET_HOURS, "hours");

  return parsed.isValid() ? parsed.valueOf() : null;
};

// Now, in the shape a datetime-local input wants. For a max= that stops a future instant
// being picked at all, rather than letting the server bounce the run.
export const getBusinessLocalNow = () => {
  return moment().utcOffset(KUWAIT_UTC_OFFSET_HOURS).format("YYYY-MM-DDTHH:mm");
};

// Epoch milliseconds -> something readable, for echoing the window back.
export const getFormatedBusinessDateTime = (value) => {
  if (!value) {
    return "";
  }

  const parsed = moment.utc(Number(value));

  if (!parsed.isValid()) {
    return String(value);
  }

  return parsed.utcOffset(KUWAIT_UTC_OFFSET_HOURS).format(DISPLAY_DATE_TIME_FORMAT);
};

export const getMaxDate = (years) => {
  return moment().subtract(years, "years")._d;
};

export const limitStrLength = (text, max_length) => {
  if (text.length > max_length - 3) {
    return text.substring(0, max_length).trimEnd() + "...";
  } else {
    return text;
  }
};

export const capitalizeOnSpace = (event, setFieldValue) => {
  const inputName = event.target.name;
  const inputValue = event.target.value;

  const nameParts = inputValue.split(" ");
  const capitalizedParts = nameParts.map((part) => {
    return part.charAt(0).toUpperCase() + part.slice(1);
  });

  const capitalizedFullName = capitalizedParts.join(" ");

  setFieldValue(inputName, capitalizedFullName);
};

export const capitalizeInitial = (event, setFieldValue) => {
  const inputName = event.target.name;
  const inputValue = event.target.value;

  const capitalizedLastName =
    inputValue.charAt(0).toUpperCase() + inputValue.slice(1);

  setFieldValue(inputName, capitalizedLastName);
};

export const urlRegex =
  /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;
export const numRegExp = /^\d+$/;
export const phoneRegExp = /^(0*[1-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
export const emailRegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
